import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "aria-label", "aria-labelledby", "children", "className", "fwdRef", "id", "label", "onChange", "small", "style"];
import React, { Children, createRef, forwardRef, isValidElement, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { deprecated } from '@op/opds.opux';
import { IconNext, IconPrevious } from '@op/opds.opux-icons/navigation';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { Button } from '../button/button';
import Tab from './tab';
const direction = {
  LEFT: -1,
  RIGHT: 1
};
const isClient = typeof window !== "undefined";
const debounceTimeOut = 150;

/**
 * _Tabs are used to change the view between related contents within the same context._
 *
 * ***
 *
 * A container for [`<Tab>`](#/Other%20Components/Tab) components. Tabs may be wrapped within other components.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<div>` element.
 */
export const Tabs = _ref => {
  let {
      active,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      children: propChildren,
      className,
      fwdRef,
      id: propId,
      label,
      onChange,
      small = false,
      style
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (label) {
    deprecated('The "label" prop of the Tabs is deprecated as of September 2024. Use "aria-label" or "aria-labelledby" instead.');
  }
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [dimensions, setDimensions] = useState({
    hasOverflow: false,
    initialised: false,
    tablist: 0,
    tabs: 0
  });
  const [stateId] = useState(() => "ds-tabs-" + uuidv4());
  const id = propId != null ? propId : stateId;
  const children = Children.toArray(propChildren).filter(child => {
    return child && child !== null;
  });
  const [amountOfChildren, setAmountOfChildren] = useState(children.length);
  const listRef = useRef(null);
  const tabRefs = useRef(children.map(() => /*#__PURE__*/createRef()));
  useEffect(() => {
    if (children.length !== amountOfChildren) {
      tabRefs.current = children.map(() => /*#__PURE__*/createRef());
      setAmountOfChildren(children.length);
    }
  }, [children, amountOfChildren]);
  function updateDimensions() {
    var _listRef$current;
    const tabs = tabRefs.current.map(ref => ref.current ? ref.current.getBoundingClientRect().width : 0 // 0 is the same as tabs initial state
    ).reduce((acc, val) => acc + val, 0);
    const tablist = listRef.current ? (_listRef$current = listRef.current) == null ? void 0 : _listRef$current.getBoundingClientRect().width : 0; // 0 is the same as tablist initial state
    const hasOverflow = tabs > tablist;
    setDimensions({
      hasOverflow,
      initialised: true,
      tablist,
      tabs
    });
  }
  useEffect(() => {
    if (!isClient || dimensions.initialised) {
      return;
    }

    // Initial
    updateDimensions();

    // On resize
    let timeoutId;
    const resizeListener = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDimensions, debounceTimeOut);
    };
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Tab marked initially active with startActive prop
  useEffect(() => {
    const [initiallyActive] = Children.map(children, (child, i) => {
      if (/*#__PURE__*/isValidElement(child)) {
        return child.props.startActive && i;
      }
    }).filter(Boolean).slice(-1);
    setActiveTabIndex(initiallyActive || 0);
  }, []);

  // Controlled Tabs with active prop
  useEffect(() => {
    if (typeof active === 'number' && active !== activeTabIndex && active >= 0 && active < children.length) {
      setActiveTabIndex(active);
    }
  }, [active]);

  // Scroll the active tab to the view
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      var _tabRefs$current$acti;
      scrollTabToPosition((_tabRefs$current$acti = tabRefs.current[activeTabIndex]) == null ? void 0 : _tabRefs$current$acti.current);
    }, debounceTimeOut);
    return () => clearTimeout(timeoutId);
  }, [activeTabIndex, dimensions.tablist]);
  useEffect(() => {
    let timeoutId;
    const listCurrent = listRef.current;
    const scrollListener = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setScrollDistance(listCurrent ? listCurrent.scrollLeft : 0),
      // 0 is the initial state of scrollDistance
      debounceTimeOut);
    };
    listCurrent == null || listCurrent.addEventListener('scroll', scrollListener);
    return () => {
      listCurrent == null || listCurrent.removeEventListener('scroll', scrollListener);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);
  function scrollToLocation(dir) {
    if (listRef.current) {
      const newPosition = listRef.current.scrollLeft + dir * dimensions.tablist;
      if (dir === direction.LEFT) {
        listRef.current.scrollLeft = newPosition > 0 ? newPosition : 0;
      } else {
        var _listRef$current2, _listRef$current3;
        // Magic number 3 spacing
        listRef.current.scrollLeft += newPosition > ((_listRef$current2 = listRef.current) == null ? void 0 : _listRef$current2.scrollWidth) ? ((_listRef$current3 = listRef.current) == null ? void 0 : _listRef$current3.scrollWidth) + 3 : newPosition;
      }
    }
  }
  function scrollTabToPosition(target) {
    const currentList = listRef.current;
    if (currentList && target) {
      if (target.offsetLeft <= currentList.scrollLeft) {
        currentList.scrollLeft = target.offsetLeft;
      } else if (target.offsetLeft + target.clientWidth >= currentList.scrollLeft + listRef.current.clientWidth) {
        const overflow = target.offsetLeft + target.clientWidth - (currentList.scrollLeft + currentList.clientWidth);
        const currentScroll = currentList.scrollLeft;
        if (currentScroll === 0) {
          // Magic number 40 button size
          currentList.scrollLeft = overflow + 40;
        } else {
          // Magic number 1 is focus outline
          currentList.scrollLeft = overflow + currentScroll + 1;
        }
      }
    }
  }
  function handleSelectTab(event, i) {
    event.preventDefault();
    scrollTabToPosition(event.target);
    setActiveTabIndex(i);
    if (onChange) {
      onChange(event, i);
    }
  }
  function handleKeyDown(event, i) {
    var _listRef$current4, _listRef$current5;
    const firstTab = (_listRef$current4 = listRef.current) == null ? void 0 : _listRef$current4.firstElementChild;
    const lastTab = (_listRef$current5 = listRef.current) == null ? void 0 : _listRef$current5.lastElementChild;
    const nextTab = event.currentTarget.nextElementSibling;
    const previousTab = event.currentTarget.previousElementSibling;
    let newFocusedTab = null;
    switch (event.key) {
      case ' ':
      case 'Enter':
        {
          handleSelectTab(event, i);
          break;
        }
      case 'Home':
        {
          newFocusedTab = firstTab;
          break;
        }
      case 'End':
        {
          newFocusedTab = lastTab;
          break;
        }
      case 'ArrowLeft':
        {
          newFocusedTab = previousTab != null ? previousTab : lastTab;
          break;
        }
      case 'ArrowRight':
        {
          newFocusedTab = nextTab != null ? nextTab : firstTab;
          break;
        }
      default:
        return;
    }
    if (newFocusedTab != null) {
      event.preventDefault();
      newFocusedTab.firstElementChild.focus();
      scrollTabToPosition(newFocusedTab);
    }
  }
  const tabsClass = buildVersionedClassNames('ds-tabs', className, {
    'ds-tabs--small': small
  });
  const firstButtonClassName = buildVersionedClassNames('ds-tabs__button', {
    // Due to buildVersionedClassNames API, the second parameter string wouldn't be versioned,
    // hence this is here, always true.
    'ds-tabs__button--first': true,
    'ds-tabs__button--hidden': !dimensions.hasOverflow || scrollDistance === 0
  });
  const lastButtonClassName = buildVersionedClassNames('ds-tabs__button', {
    // Due to buildVersionedClassNames API, the second parameter string wouldn't be versioned,
    // hence this is here, always true.
    'ds-tabs__button--last': true,
    'ds-tabs__button--hidden': !dimensions.hasOverflow || listRef.current && scrollDistance === listRef.current.scrollWidth - listRef.current.clientWidth
  });
  const tabId = (child, i) => child.props.id || id + "-tab-" + i;
  const panelId = (child, i) => {
    var _child$props$panelPro;
    return ((_child$props$panelPro = child.props.panelProps) == null ? void 0 : _child$props$panelPro.id) || id + "-panel-" + i;
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    className: tabsClass,
    id: id,
    ref: fwdRef
  }, props), /*#__PURE__*/React.createElement("div", {
    className: buildVersionedClass('ds-tabs__nav'),
    style: style
  }, !small && /*#__PURE__*/React.createElement(Button, {
    "aria-hidden": "true",
    className: firstButtonClassName,
    ghost: true,
    icon: IconPrevious,
    iconLabel: "Label",
    onClick: () => {
      scrollToLocation(direction.LEFT);
    },
    size: "default",
    tabIndex: -1 // To avoid keyboard focus
    ,
    type: "button"
  }), /*#__PURE__*/React.createElement("div", {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    className: buildVersionedClass('ds-tabs__nav__list'),
    ref: listRef,
    role: "tablist"
  }, Children.map(children, (child, i) => {
    if (/*#__PURE__*/isValidElement(child)) {
      return /*#__PURE__*/React.cloneElement(child, {
        active: activeTabIndex === i,
        'aria-controls': panelId(child, i),
        'aria-selected': activeTabIndex === i,
        id: tabId(child, i),
        index: i,
        key: i,
        onKeyDown: handleKeyDown,
        onSelect: handleSelectTab,
        ref: tabRefs.current[i],
        role: 'tab',
        small: small
      },
      // Fix adapted from https://stackoverflow.com/a/31816062
      child.props.label);
    }
  })), !small && /*#__PURE__*/React.createElement(Button, {
    "aria-hidden": "true",
    className: lastButtonClassName,
    ghost: true,
    icon: IconNext,
    iconLabel: "Label",
    onClick: () => {
      scrollToLocation(direction.RIGHT);
    },
    size: "default",
    tabIndex: -1 // To avoid keyboard focus
    ,
    type: "button"
  })), Children.map(children, (child, i) => {
    if (/*#__PURE__*/isValidElement(child)) {
      return /*#__PURE__*/React.createElement("div", _extends({
        "aria-hidden": activeTabIndex !== i,
        "aria-labelledby": tabId(child, i),
        className: buildVersionedClass('ds-tabs__panel') // Needed for focus appearance
        ,
        id: id + "-panel-" + i,
        key: child.key,
        role: "tabpanel",
        style: {
          display: activeTabIndex === i ? 'block ' : 'none'
        },
        tabIndex: 0
      }, child.props.panelProps), child.props.children);
    }
  }));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(Tabs, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'Tabs';
export default Fwd;
export { Tab };